"use client"

import { ApertureStyles, T, MAXW, SectionEyebrow } from "./_shared"

/**
 * Problem section — sits between the hero and IndustryStats.
 *
 * The PAS framework (Problem-Agitate-Solution) lives across the next
 * three sections: this one names the pain in concrete numbers, the
 * Validators section delivers the solution, the FinalCta closes.
 *
 * Visitors don't always KNOW their list has a deliverability problem
 * until they hit a hard-bounce wall. This block makes the problem
 * legible in one scroll without being doom-scrolling negative —
 * three short stat cards, one editorial line of agitation, done.
 */
export default function ApertureProblem() {
  const stats: { metric: string; label: string; sub: string }[] = [
    {
      metric: "5%",
      label: "is the bounce rate that gets you flagged",
      sub: "Most ESPs throttle your sends past this. Some suspend the account.",
    },
    {
      metric: "gmial",
      label: "is all it takes to lose a real contact",
      sub: "Typos like gmial.com, hotnail.com, and yaho.com look close enough to miss in a spreadsheet.",
    },
    {
      metric: "MX",
      label: "tells you whether the domain can receive mail",
      sub: "A valid-looking address can still point at a parked, expired, or typo'd domain.",
    },
  ]

  return (
    <section className="relative" style={{ background: T.bgSoft }}>
      <ApertureStyles />
      <div className={`${MAXW} mx-auto px-6 lg:px-10 py-12 sm:py-16 md:py-24`}>
        <div className="max-w-3xl">
          <SectionEyebrow>The hidden problem</SectionEyebrow>
          <h2
            className="mt-3 leading-[1.0] tracking-[-0.035em]"
            style={{ fontSize: "clamp(2rem, 4vw, 3.1rem)", fontWeight: 800, color: T.ink }}
          >
            Your bounce rate is silently{" "}
            <em style={{ color: T.bad, fontStyle: "italic", fontWeight: 700 }}>
              destroying your sender reputation.
            </em>
          </h2>
          <p
            className="mt-5 leading-relaxed"
            style={{ fontSize: 16, color: T.ink2, maxWidth: "60ch" }}
          >
            Your ESP charges you to send — whether the inbox exists or not.
            Every bounce ticks your sender score down. By the time Gmail
            puts you in the spam folder, the damage is months old.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats.map((s) => (
            <div
              key={s.metric}
              className="relative rounded-2xl p-6 overflow-hidden"
              style={{
                background: T.surface,
                border: `1px solid ${T.hair}`,
                boxShadow: `0 1px 0 rgba(255,255,255,0.6) inset, 0 16px 40px -28px rgba(10,10,10,0.18)`,
              }}
            >
              {/* Mint hairline at the top — same editorial cue every card uses */}
              <div
                aria-hidden
                className="absolute inset-x-6 top-0 h-px pointer-events-none"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, rgba(160,43,31,0.40), transparent)",
                }}
              />
              <p
                className="tabular-nums leading-none"
                style={{
                  fontSize: "clamp(2.4rem, 4vw, 3rem)",
                  fontWeight: 800,
                  letterSpacing: "-0.04em",
                  color: T.bad,
                }}
              >
                {s.metric}
              </p>
              <p
                className="mt-3"
                style={{ fontSize: 14, fontWeight: 600, color: T.ink, lineHeight: 1.4 }}
              >
                {s.label}
              </p>
              <p
                className="mt-1.5"
                style={{ fontSize: 12.5, color: T.ink3, lineHeight: 1.5 }}
              >
                {s.sub}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
